// Bucknell University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Bucknell's Common Data Set 2025-2026 (common_data_set_2025-26.pdf),
//    linked as "Common Data Set 2025-26" from the Office of Institutional
//    Research & Analytics' Institutional Data page: C1 applicants, admits
//    and enrollees for Fall 2025 with the residency table (2 applicants of
//    unknown residency sit in the total only), C8 test policy for Fall
//    2027, C21 early decision counts for the Fall 2025 class (one count for
//    ED I and II together), C22 (no early action), G1/G5 2026-2027 costs
//    (not marked provisional).
//    Quirk: every page of the file still carries the running header "Common
//    Data Set 2024-2025", but its questions are the 2025-2026 edition's
//    (Fall 2025 counts, testing for Fall 2027, 2026-2027 costs) and the
//    institutional page lists it as 2025-26.
//  - Bucknell Admissions: Apply to Bucknell (testing, the three colleges,
//    the binding Early Decision terms), Admissions Dates & Deadlines
//    ("2026-27 Dates & Deadlines": deadlines, decision release, deposits,
//    the test-optional pilot note) and Undergraduate Admission Requirements
//    (college and major course requirements).
//  - College Scorecard, school id 211291 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Bucknell Common Data Set 2025-2026',
        url: 'https://www.bucknell.edu/azdirectory/office-institutional-research-analytics/institutional-data',
        read: '2026-10-01',
    },
    apply: {
        label: 'Bucknell Admissions, Apply to Bucknell',
        url: 'https://www.bucknell.edu/admissions-aid/apply-bucknell',
        read: '2026-10-01',
    },
    dates: {
        label: 'Bucknell Admissions, Admissions Dates & Deadlines',
        url: 'https://www.bucknell.edu/admissions-aid/admissions-dates-deadlines',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Bucknell Admissions, Undergraduate Admission Requirements',
        url: 'https://www.bucknell.edu/admissions-aid/apply-bucknell/undergraduate-admission-requirements',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?211291-Bucknell-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'bucknell-university',
    kbId: 'bucknell_university',
    name: 'Bucknell University',
    shortName: 'Bucknell',
    location: 'Lewisburg, PA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Bucknell brand color palette); decoration only.
    branding: {
        primary: '#003865',
        secondary: '#EF5B0C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/bucknell_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 11561,
        admitted: 3723,
        enrolled: 1037,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2044, admitted: 751 },
            { group: 'Out-of-state', applied: 6884, admitted: 2816 },
            { group: 'International', applied: 2631, admitted: 156 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (2 applicants of unknown residency are in the total only)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Bucknell\'s Common Data Set reports one first-year acceptance rate for the university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early Decision',
        note: 'The Common Data Set gives one count for Early Decision I and II together. Bucknell has no early action plan.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision I and II', applied: 1200, admitted: 656 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of Bucknell\'s three colleges, all on the same application, and choose an intended major or "undecided" within it:',
        rows: [
            { school: 'College of Engineering', requirement: 'One year of chemistry or physics and one year of precalculus; Bucknell strongly recommends at least three years of science, including both chemistry and physics. Moving into engineering later from another college is limited.' },
            { school: 'Freeman College of Management', requirement: 'Enrollment is limited to students admitted directly into the college when they first apply to Bucknell.' },
            { school: 'Math, science, engineering, accounting, business analytics and economics majors', requirement: 'One year of precalculus, and preferably calculus, on top of the courses every applicant needs.' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 10' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Early December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 10', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 10', decision: 'Mid-March', binding: false },
        ],
        note: 'The application opens August 1, 2026, on the Common Application or the Coalition Application (powered by Scoir). The scholarship application, CSS Profile and FAFSA are due with the application in each round. Enrollment deposits are due in mid-December (Early Decision I), late February (Early Decision II) and May 1, 2027 (Regular Decision).',
        source: 'dates',
        binding: 'Early Decision I and II are binding: if admitted, you must accept and withdraw your applications to other colleges. If your application is moved to Regular Decision, you are no longer bound; if you are denied, you cannot reapply until the next cycle. You can switch a Regular Decision application to Early Decision I or II with the Common App or Coalition App agreement.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Bucknell does not require SAT or ACT scores, though you are welcome to send them. Bucknell calls its test-optional policy a pilot: if you enroll and have taken the SAT or ACT, you must send official scores, which are used only for research and do not change your admission decision.',
        source: 'dates',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 72600 },
            { item: 'Required fees', amount: 900 },
            { item: 'Food and housing (on campus)', amount: 18380 },
            { item: 'Books and supplies', amount: 900 },
            { item: 'Transportation', amount: 800 },
            { item: 'Other expenses', amount: 2000 },
        ],
        total: 95580,
        note: 'For a full-time undergraduate living on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 50021,
            byIncome: [
                { income: '$0 to $30,000', amount: 17578 },
                { income: '$30,001 to $48,000', amount: 24370 },
                { income: '$48,001 to $75,000', amount: 27496 },
                { income: '$75,001 to $110,000', amount: 34392 },
                { income: 'Over $110,000', amount: 67428 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Bucknell\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
